import { EstimateFinanceOptionRecord } from "common/records/EstimateFinanceOption";
import { RootState } from "common/reducers";
import { Nullable } from "common/types";
import { currentEstimate, estimate } from "common/selectors/estimate.selectors";
import { List, Map } from "immutable";
import { createSelector } from "reselect";

const rootKey = "estimateFinanceOptions";

const EMPTY_FINANCE_OPTION_IDS = List<number>();

/**
 * Returns the byId map from state
 * @param state The RootState
 * @returns the byId Map
 */
export const byId = (state: RootState): Map<number, EstimateFinanceOptionRecord> => state.getIn([rootKey, "byId"]);

/**
 * Returns the lastSavedById map from state
 * @param state The RootState
 * @returns the lastSavedById Map
 */
export const lastSavedById = (state: RootState): Map<number, EstimateFinanceOptionRecord> =>
  state.getIn([rootKey, "lastSavedById"]);

/**
 * Returns the current unsaved ID for creating new estimate finance options
 * @param state The RootState
 * @returns The current unsaved ID (negative number)
 */
export const unsavedId = (state: RootState): number => state.getIn([rootKey, "unsavedId"]) as number;

/**
 * Returns the estimate finance option record for the given id
 * @param state The RootState
 * @param props The estimateFinanceOptionId
 * @returns the estimate finance option record
 */
export const estimateFinanceOption = (
  state: RootState,
  props: { estimateFinanceOptionId: number },
): EstimateFinanceOptionRecord | undefined => byId(state)?.get(props.estimateFinanceOptionId);

/**
 * Returns the list of estimate finance option records for the given ids
 * Filters out records marked for destruction unless fullEstimate is true
 * @param state The RootState
 * @param props The estimateFinanceOptionIds and optional fullEstimate flag
 * @returns the estimate finance options List
 */
export const estimateFinanceOptions = createSelector(
  [
    byId,
    (_: RootState, props: { estimateFinanceOptionIds: List<number>; fullEstimate?: boolean }) =>
      props.estimateFinanceOptionIds,
    (_: RootState, props: { estimateFinanceOptionIds: List<number>; fullEstimate?: boolean }) => props.fullEstimate,
  ],
  (byId, estimateFinanceOptionIds, fullEstimate): List<EstimateFinanceOptionRecord> => {
    if (!estimateFinanceOptionIds) {
      return List();
    }

    return estimateFinanceOptionIds
      .map((id: number) => byId.get(id))
      .filter((efo): efo is EstimateFinanceOptionRecord => {
        if (!efo) return false;
        return fullEstimate || !efo._destroy;
      })
      .toList();
  },
);

/** The current estimate's first live (non-`_destroy`) estimate finance option, or null. */
export const currentEstimateFirstFinanceOption = createSelector(
  [currentEstimate, byId],
  (estimate, byId): Nullable<EstimateFinanceOptionRecord> => {
    if (!estimate) return null;

    return (
      estimate.finance_option_ids
        .map((id) => byId.get(id))
        .find((efo): efo is EstimateFinanceOptionRecord => Boolean(efo) && !efo._destroy) ?? null
    );
  },
);

export const estimateFinanceOptionIds = createSelector([estimate, byId], (estimate, byId): List<number> => {
  if (!estimate) {
    return EMPTY_FINANCE_OPTION_IDS;
  }

  return estimate.finance_option_ids
    .map((id) => byId.get(id))
    .filter((efo): efo is EstimateFinanceOptionRecord => Boolean(efo) && !efo._destroy)
    .map((efo) => efo.finance_option_id);
});

/**
 * Returns the list of last saved estimate finance option records for the given ids
 * @param state The RootState
 * @param props The estimateFinanceOptionIds
 * @returns the last saved estimate finance options List
 */
export const lastSavedEstimateFinanceOptions = createSelector(
  [lastSavedById, (_: RootState, props: { estimateFinanceOptionIds: List<number> }) => props],
  (lastSavedById, props): List<EstimateFinanceOptionRecord> => {
    const { estimateFinanceOptionIds } = props;

    if (!estimateFinanceOptionIds) {
      return List();
    }

    return estimateFinanceOptionIds
      .map((id: number) => lastSavedById.get(id))
      .filter((efo): efo is EstimateFinanceOptionRecord => !!efo)
      .toList();
  },
);

/**
 * An allowlist: a new field must be added here and to the controller's permit list, in that order.
 *
 * @param state The RootState
 * @param props The estimateFinanceOptionIds
 * @returns
 */
export const toEstimateFinanceOptionsJson = createSelector(
  [
    (state: RootState, props: { estimateFinanceOptionIds: List<number> }) =>
      estimateFinanceOptions(state, { ...props, fullEstimate: true }),
  ],
  (estimateFinanceOptions) => {
    return estimateFinanceOptions
      .map((efo) => {
        const payload: Record<string, unknown> = {
          finance_option_id: efo.finance_option_id,
          monthly_payment: efo.monthly_payment,
          promo_payment: efo.promo_payment,
        };

        // No id makes Rails create; an id alongside _destroy makes it destroy.
        if (efo.id > 0) {
          payload.id = efo.id;
        }
        if (efo._destroy) {
          payload._destroy = efo._destroy;
        }

        return payload;
      })
      .toArray();
  },
);
